import { useEffect, useRef, useState } from "react";
import api from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { ClientLayout, BlockedBanner } from "./ClientHome";
import { PdfViewerModal } from "@/components/PdfViewerModal";
import { Apple, Check, Flame, Plus, Trash2, ScanBarcode, X, Sunrise, Coffee, Sun, MoonStar, RefreshCw, ChefHat } from "lucide-react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/api";
import { Html5Qrcode } from "html5-qrcode";

const DAYS_IT = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const DAYS_SHORT = { "Lunedì": "Lun", "Martedì": "Mar", "Mercoledì": "Mer", "Giovedì": "Gio", "Venerdì": "Ven", "Sabato": "Sab", "Domenica": "Dom" };
const MEAL_ICON = (label) => {
  const l = label.toLowerCase();
  if (l.includes("colazione")) return Sunrise;
  if (l.includes("pranzo")) return Sun;
  if (l.includes("cena")) return MoonStar;
  return Coffee;
};

export default function ClientNutrition() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [checks, setChecks] = useState({});
  const [foods, setFoods] = useState([]);
  const [log, setLog] = useState(null);
  const [entry, setEntry] = useState({ name: "", grams: "" });
  const [selected, setSelected] = useState(null);
  const [openList, setOpenList] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [selDay, setSelDay] = useState(() => DAYS_IT[(new Date().getDay() + 6) % 7]);
  const [off, setOff] = useState([]);
  const [offLoading, setOffLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const stopScanner = async () => {
    try {
      if (scannerRef.current) await scannerRef.current.stop();
    } catch {}
    scannerRef.current = null;
    setScanning(false);
  };

  const startScanner = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("barcode-scanner");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 120 } },
          async (code) => {
            await stopScanner();
            try {
              const { data: prod } = await api.get(`/foods/barcode/${code}`);
              await pickOff(prod);
            } catch (e) {
              toast.error(e?.response?.status === 404 ? t("nu.scanNotFound") : formatApiError(e));
            }
          },
          () => {}
        );
      } catch {
        setScanning(false);
        toast.error(t("chat.micDenied"));
      }
    }, 100);
  };

  const searchOff = async () => {
    setOffLoading(true);
    try {
      const { data } = await api.get(`/foods/search-online?q=${encodeURIComponent(entry.name.trim())}`);
      setOff(data);
      if (!data.length) toast.info(t("nu.onlineEmpty"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOffLoading(false);
    }
  };

  const pickOff = async (f) => {
    try {
      const { data } = await api.post("/foods/import", f);
      setSelected(data);
      setEntry({ ...entry, name: data.name });
      setFoods((prev) => [...prev, data]);
      setOff([]);
      setOpenList(false);
      toast.success(t("nu.onlineImport"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const q = entry.name.trim().toLowerCase();
  const matches = q
    ? foods
        .filter((f) => f.name.toLowerCase().includes(q))
        .sort((a, b) => {
          const rank = (f) => {
            const n = f.name.toLowerCase();
            if (n.startsWith(q)) return 0;
            if (n.includes(` ${q}`)) return 1;
            return 2;
          };
          return rank(a) - rank(b) || a.name.localeCompare(b.name);
        })
        .slice(0, 8)
    : [];

  const loadLog = () => api.get(`/client/food-log?date=${today}`).then((r) => setLog(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/client/nutrition").then((r) => setData(r.data)).catch(() => setData({ blocked: true }));
    api.get(`/client/nutrition/checks?date=${today}`).then((r) => setChecks(r.data)).catch(() => {});
    api.get("/foods").then((r) => setFoods(r.data)).catch(() => {});
    loadLog();
  }, [today]);

  const addEntry = async () => {
    try {
      await api.post("/client/food-log", {
        food_name: selected?.name || entry.name,
        grams: Number(String(entry.grams).replace(",", ".")),
        date: today,
      });
      setEntry({ name: "", grams: "" });
      setSelected(null);
      loadLog();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const delEntry = async (id) => {
    await api.delete(`/client/food-log/${id}`);
    loadLog();
  };

  const toggle = async (mealId) => {
    const done = !checks[mealId];
    setChecks((prev) => ({ ...prev, [mealId]: done }));
    try {
      await api.post("/client/nutrition/check", { meal_id: mealId, date: today, done });
    } catch (e) {
      setChecks((prev) => ({ ...prev, [mealId]: !done }));
    }
  };

  if (!data) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;
  if (data.blocked) return <ClientLayout><BlockedBanner /></ClientLayout>;

  const plan = data.plan;

  return (
    <ClientLayout>
      <div data-testid="client-nutrition-page" className="max-w-2xl mx-auto w-full space-y-6">
        <PdfViewerModal url={pdfUrl} title={t("nu.yourPlan")} onClose={() => setPdfUrl("")} />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-3xl font-black uppercase tracking-tight">{t("nu.yourPlan")}</h1>
          {plan && (
            <button data-testid="download-nutrition-pdf" onClick={() => setPdfUrl(`${process.env.REACT_APP_BACKEND_URL}/api/client/nutrition/pdf`)}
              className="kc-btn-ghost !px-4 !py-2.5 text-sm inline-flex items-center gap-2 !text-[#F5E6E8] !border-[#453740]">
              <Download className="w-4 h-4" /> {t("nu.downloadPdf")}
            </button>
          )}
        </div>
        {!plan ? (
          <p className="text-muted-foreground" data-testid="nutrition-no-plan">{t("nu.noPlan")}</p>
        ) : (
          <>
            <div className="kc-card p-5" data-testid="nutrition-targets">
              <p className="kc-eyebrow mb-3 flex items-center gap-2"><Flame className="w-4 h-4" />{t("nu.targets")}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[[plan.kcal_target, "kcal"], [plan.protein_g, "P (g)"], [plan.carbs_g, "C (g)"], [plan.fat_g, "F (g)"]].map(([v, label]) => (
                  <div key={label} className="bg-secondary/60 rounded-xl p-3 text-center">
                    <p className="font-mono text-2xl font-black">{v ?? "—"}</p>
                    <p className="text-xs text-muted-foreground uppercase">{label}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-3">{plan.name}</p>
            </div>

            <section className="kc-card p-5 space-y-4 kc-animate" data-testid="calorie-counter">
              <p className="kc-eyebrow flex items-center gap-2"><Flame className="w-4 h-4" />{t("nu.counter")} · {today}</p>
              {log && log.targets?.kcal && (() => {
                const remKcal = Math.round(log.targets.kcal - log.totals.kcal);
                const remP = Math.max(0, Math.round((log.targets.protein ?? 0) - log.totals.protein));
                const remC = Math.max(0, Math.round((log.targets.carbs ?? 0) - log.totals.carbs));
                const remF = Math.max(0, Math.round((log.targets.fat ?? 0) - log.totals.fat));
                const over = remKcal < 0;
                return (
                  <div className={`rounded-2xl p-4 text-center ${over ? "bg-destructive/10 border border-destructive/30" : "kc-card-dark"}`}
                    data-testid="calorie-remaining-banner">
                    <p className="text-xs font-bold uppercase tracking-wider opacity-70">{over ? t("nu.overTarget") : t("nu.remaining")}</p>
                    <p className={`font-mono text-4xl font-black ${over ? "text-destructive" : "text-[#9C2745]"}`} data-testid="calorie-remaining-kcal">
                      {over ? `+${Math.abs(remKcal)}` : remKcal} <span className="text-base font-normal">kcal</span>
                    </p>
                    <p className={`text-xs font-mono mt-1 ${over ? "text-destructive/80" : "opacity-60"}`} data-testid="calorie-remaining-macros">
                      P {remP}g · C {remC}g · F {remF}g
                    </p>
                  </div>
                );
              })()}
              {log && (
                <div className="space-y-2" data-testid="calorie-progress">
                  {[["kcal", log.totals.kcal, log.targets?.kcal, ""], ["P", log.totals.protein, log.targets?.protein, "g"],
                    ["C", log.totals.carbs, log.targets?.carbs, "g"], ["F", log.totals.fat, log.targets?.fat, "g"]].map(([label, val, target, unit]) => {
                    const pct = target ? Math.min(100, Math.round((val / target) * 100)) : 0;
                    return (
                      <div key={label} className="flex items-center gap-3" data-testid={`calorie-bar-${label.toLowerCase()}`}>
                        <span className="w-8 text-xs font-bold uppercase">{label}</span>
                        <div className="flex-1 h-2.5 bg-secondary rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-500 ${target && val > target ? "bg-destructive" : "bg-primary"}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-xs text-muted-foreground w-28 text-right">
                          {Math.round(val * 10) / 10}{unit} / {target ?? "—"}{unit}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input data-testid="calorie-food-input" placeholder={t("nu.foodSearch")}
                      value={entry.name}
                      onChange={(e) => { setEntry({ ...entry, name: e.target.value }); setSelected(null); setOpenList(true); }}
                      onFocus={() => setOpenList(true)}
                      onBlur={() => setTimeout(() => setOpenList(false), 200)}
                      className="kc-input" />
                    {openList && matches.length > 0 && (
                      <ul className="absolute z-20 inset-x-0 top-full mt-1 kc-card bg-popover max-h-56 overflow-y-auto" data-testid="food-search-results">
                        {matches.map((f) => (
                          <li key={f._id}>
                            <button type="button" data-testid="food-result-item"
                              onMouseDown={() => { setSelected(f); setEntry({ ...entry, name: f.name }); setOpenList(false); }}
                              className="w-full text-left px-4 py-2.5 hover:bg-secondary/60 text-sm flex justify-between gap-2">
                              <span>{f.name}</span>
                              <span className="font-mono text-muted-foreground shrink-0">{f.kcal} kcal/100g</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {openList && q.length >= 3 && (
                      <button type="button" data-testid="food-online-search-btn" onMouseDown={searchOff} disabled={offLoading}
                        className="absolute -bottom-6 left-0 text-xs font-bold text-primary hover:underline">
                        {offLoading ? t("common.loading") : t("nu.searchOnline")}
                      </button>
                    )}
                    {off.length > 0 && (
                      <ul className="absolute z-20 inset-x-0 top-full mt-1 kc-card bg-popover max-h-56 overflow-y-auto" data-testid="food-online-results">
                        {off.map((f, i) => (
                          <li key={i}>
                            <button type="button" data-testid={`food-online-item-${i}`}
                              onMouseDown={() => pickOff(f)}
                              className="w-full text-left px-4 py-2.5 hover:bg-secondary/60 text-sm flex justify-between gap-2">
                              <span className="truncate">{f.name}</span>
                              <span className="font-mono text-muted-foreground shrink-0">{f.kcal} kcal/100g</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <input data-testid="calorie-grams-input" inputMode="decimal" placeholder={t("nu.grams")}
                    value={entry.grams} onChange={(e) => setEntry({ ...entry, grams: e.target.value })} className="kc-input !w-24 font-mono" />
                  <button data-testid="barcode-scan-button" onClick={startScanner} title={t("nu.scan")}
                    className="kc-btn-ghost !px-3 !py-2 border-2 border-border rounded-xl text-primary">
                    <ScanBarcode className="w-5 h-5" />
                  </button>
                  <button data-testid="calorie-add-button" onClick={addEntry}
                    disabled={!entry.name.trim() || !(Number(String(entry.grams).replace(",", ".")) > 0)} className="kc-btn-primary !px-4 !py-2">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {selected && (
                  <p className="text-xs font-mono text-accent" data-testid="food-selected-info">
                    {selected.name}: {selected.kcal} kcal · P {selected.protein}g · C {selected.carbs}g · F {selected.fat}g /100g
                  </p>
                )}
              </div>
              {log && log.entries.length > 0 ? (
                <ul className="divide-y divide-border" data-testid="calorie-entries">
                  {log.entries.map((e, i) => (
                    <li key={e._id} className="py-2 flex items-center gap-3 text-sm" data-testid={`calorie-entry-${i}`}>
                      <span className="flex-1">{e.food_name}</span>
                      <span className="font-mono text-muted-foreground">{e.grams}g</span>
                      <span className="font-mono font-bold">{e.kcal} kcal</span>
                      <button data-testid={`calorie-delete-${i}`} onClick={() => delEntry(e._id)}
                        className="kc-btn-ghost !p-1 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="calorie-empty">{t("nu.noEntries")}</p>
              )}
            </section>

            {(() => {
              const isWeekly = (plan.meals || []).some((m) => DAYS_IT.includes(m.name));
              if (!isWeekly) {
                return (
                  <div className="space-y-3">
                    {(plan.meals || []).map((m, i) => (
                      <div key={m.id} className={`kc-card p-4 ${checks[m.id] ? "border-accent/60" : ""}`} data-testid={`client-meal-${i}`}>
                        <div className="flex items-center gap-3">
                          <button data-testid={`meal-check-${i}`} onClick={() => toggle(m.id)}
                            className={`w-10 h-10 rounded-xl border-2 grid place-items-center shrink-0 transition-colors ${
                              checks[m.id] ? "bg-accent border-accent text-accent-foreground" : "border-border text-transparent hover:border-accent/60"
                            }`}>
                            <Check className="w-5 h-5" />
                          </button>
                          <div className="flex-1">
                            <p className="font-bold">{m.name} {m.time && <span className="text-sm text-muted-foreground font-mono">· {m.time}</span>}</p>
                            <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
                              {(m.foods || []).map((f, fi) => (
                                <li key={fi} className="flex justify-between gap-4">
                                  <span>{f.name}</span>
                                  <span className="font-mono">{f.quantity}g{f.kcal != null ? ` · ${f.kcal} kcal` : ""}</span>
                                </li>
                              ))}
                            </ul>
                            {(() => {
                              const tot = (m.foods || []).reduce((a, f) => a + (f.kcal || 0), 0);
                              return tot > 0 && <p className="text-xs font-mono text-accent mt-1">{t("nu.mealTotal")}: {tot} kcal</p>;
                            })()}
                            {m.notes && <p className="text-xs text-muted-foreground italic mt-1">"{m.notes}"</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              const weekDays = DAYS_IT.map((n) => (plan.meals || []).find((m) => m.name === n)).filter(Boolean);
              const day = weekDays.find((d) => d.name === selDay) || weekDays[0];
              const groups = {};
              (day?.foods || []).forEach((f) => {
                const idx = f.name.indexOf(" · ");
                const lbl = idx > 0 ? f.name.slice(0, idx) : "Pasto";
                const clean = idx > 0 ? f.name.slice(idx + 3) : f.name;
                (groups[lbl] = groups[lbl] || []).push({ ...f, clean });
              });
              const order = ["Colazione", "Spuntino", "Spuntino mattina", "Pranzo", "Spuntino pomeriggio", "Cena"];
              const mealsOfDay = Object.entries(groups).sort((a, b) => {
                const ia = order.indexOf(a[0]), ib = order.indexOf(b[0]);
                return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
              });
              const dayKcal = (day?.foods || []).reduce((a, f) => a + (f.kcal || 0), 0);
              return (
                <div className="space-y-4" data-testid="weekly-plan">
                  <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="day-tabs">
                    {weekDays.map((d) => (
                      <button key={d.id} data-testid={`day-tab-${d.name}`} onClick={() => setSelDay(d.name)}
                        className={`px-3.5 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 transition-colors ${
                          selDay === d.name ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground"
                        }`}>
                        {DAYS_SHORT[d.name] || d.name}
                      </button>
                    ))}
                  </div>
                  {day && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-lg font-black uppercase tracking-tight">{day.name}
                          {dayKcal > 0 && <span className="font-mono text-xs text-muted-foreground font-normal ml-2">{dayKcal} kcal</span>}
                        </p>
                        <button data-testid="day-check" onClick={() => toggle(day.id)}
                          className={`kc-btn-ghost !px-3 !py-1.5 text-xs inline-flex items-center gap-1.5 shrink-0 ${checks[day.id] ? "!border-accent !text-accent" : "!text-[#F5E6E8] !border-[#453740]"}`}>
                          <Check className="w-3.5 h-3.5" /> {checks[day.id] ? t("nu.dayDoneOn") : t("nu.dayDone")}
                        </button>
                      </div>
                      {mealsOfDay.map(([label, foods]) => {
                        const Icon = MEAL_ICON(label);
                        const tot = foods.reduce((a, f) => a + (f.kcal || 0), 0);
                        return (
                          <div key={label} className="kc-card-dark p-4" data-testid={`daymeal-${label}`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-bold text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-[#E0597F]" />{label}</p>
                              {tot > 0 && <span className="font-mono text-xs opacity-60">{tot} kcal</span>}
                            </div>
                            <ul className="space-y-1">
                              {foods.map((f, fi) => (
                                <li key={fi} className="flex justify-between gap-3 text-sm">
                                  <span className="opacity-90">{f.clean}</span>
                                  <span className="font-mono text-xs opacity-60 shrink-0">{f.quantity}g</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                      {day.notes && (
                        <div className="kc-card p-4 space-y-2" data-testid="day-notes">
                          {day.notes.split(" | ").map((part, i) => {
                            const ci = part.indexOf(":");
                            const k = ci > 0 ? part.slice(0, ci).trim() : "";
                            const v = ci > 0 ? part.slice(ci + 1).trim() : part;
                            return (
                              <p key={i} className="text-xs">
                                {k && <span className="font-black uppercase tracking-wider text-primary">{k} — </span>}
                                <span className="text-muted-foreground">{v}</span>
                              </p>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {(plan.substitutions || []).length > 0 && (
              <section className="kc-card p-5 kc-animate" data-testid="substitutions-card">
                <p className="kc-eyebrow mb-3 flex items-center gap-2"><RefreshCw className="w-4 h-4" />{t("nu.substitutions")}</p>
                <p className="text-xs text-muted-foreground mb-3">{t("nu.substHint")}</p>
                <div className="divide-y divide-border" data-testid="substitutions-table">
                  {plan.substitutions.map((s, i) => (
                    <div key={i} className="py-2.5 flex gap-3 text-sm" data-testid={`subst-row-${i}`}>
                      <span className="font-bold w-2/5 shrink-0">{s.food}</span>
                      <span className="text-muted-foreground">{s.alternatives}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {(plan.recipes || []).length > 0 && (
              <section className="kc-card p-5 kc-animate" data-testid="recipes-card">
                <p className="kc-eyebrow mb-3 flex items-center gap-2"><ChefHat className="w-4 h-4" />{t("nu.recipes")}</p>
                <div className="space-y-4" data-testid="recipes-list">
                  {plan.recipes.map((r, i) => (
                    <div key={i} data-testid={`recipe-card-${i}`}>
                      <p className="font-bold text-sm">{r.name}</p>
                      {(r.ingredients || []).length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1"><span className="font-semibold">{t("nu.recipeIngredientsShort")}:</span> {r.ingredients.join(", ")}</p>
                      )}
                      {r.preparation && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{r.preparation}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Apple className="w-3.5 h-3.5" />{t("nu.today")}: {today}</p>
          </>
        )}
        {scanning && (
          <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" data-testid="barcode-scanner-modal">
            <div className="kc-card p-4 w-full max-w-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-sm">{t("nu.scanHint")}</p>
                <button onClick={stopScanner} data-testid="barcode-scanner-close" className="kc-btn-ghost !p-1.5"><X className="w-5 h-5" /></button>
              </div>
              <div id="barcode-scanner" className="w-full rounded-xl overflow-hidden" />
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
